import JsPDF, { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
applyPlugin(JsPDF);
(function (jsPDFAPI) {
  // 1) ZWJ + special-character helper
  const zwj = "\u200D";
  const specials = ["\u06A8", "\u0651"]; // ڨ (GAF), SHADDA

  function fixArabicLigatures(s) {
    let out = "";
    for (let i = 0; i < s.length; i++) {
      out += s[i];
      if (
        s[i + 1] &&
        (specials.includes(s[i]) || specials.includes(s[i + 1]))
      ) {
        out += zwj;
      }
    }
    return out;
  }

  // 2) Shaping wrapper
  function shapeIfArabic(txt) {
    // Only shape if there’s at least one Arabic code-point
    if (!/[\u0600-\u06FF]/.test(txt)) {
      return txt;
    }
    // Make sure the plugin is there
    if (typeof jsPDFAPI.processArabic !== "function") {
      console.error("Arabic plugin missing! Include modules/arabic.js first.");
      return txt;
    }
    // Reverse for RTL + shape
    return jsPDFAPI.processArabic(fixArabicLigatures(txt), true);
  }

  // 3) Register your shaper on every call to doc.text()
  jsPDFAPI.events.push([
    "preProcessText",
    function (originalText /*, renderOptions */) {
      // return the (possibly transformed) string
      return shapeIfArabic(originalText);
    },
  ]);
})(jsPDF.API);

import moment from "moment";
import defaultTemplate from "./templates/default.json";
import { cloneDeep } from "lodash";
const headerTrasnlator = {
  birthDate: " :تاريخ الولادة",
  schoolarYear: " :السنة الدراسيّة",
  className: " :القسم",
  nbInscription: " :عدد التلاميذ المرسّمين",
  uniqueIden: " :المعرف الوحيد",
  studentName: (gender) => (gender === "male" ? " :التلميذ" : " :التلميذة"),
  schoolName: (selectedLevel, force = false) => {
    if (force) return " :المعهد الخاص";
    if (selectedLevel == -1 || selectedLevel == -2) return ": الروضة الخاصة";
    return selectedLevel <= 9 ? " : المدرسة الابتدائية الخاصة" : " : المعهد الخاص ";
  },
};
function getImageBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
}
async function imageUrlToBase64(url) {
  try {
    if (!url) throw new Error("no URL");
    //if (!url || !(typeof url == "string")) throw new Error("no URL");
    const response = await fetch(url);
    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result); // this is the Base64 string
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
}

function decideSpacing(startY, endY, modules) {
  const moduleHeaderHeight = 8;
  const subjectHeadersHeight = 8;
  const availableHeight = endY - startY;
  const minSpacing = 6; // Minimum spacing between modules
  const maxSpacing = 15; // Maximum spacing between modules
  let nbLoneSubjects = 0;
  let NbSubSubjects = 0;
  let nbParentSubjects = 0;

  let usedH = (moduleHeaderHeight + subjectHeadersHeight) * modules.length;
  modules.forEach((module) => {
    module.subjects.forEach((subject) => {
      if (subject.subSubjectMarks && subject.subSubjectMarks.length > 0) {
        NbSubSubjects += subject.subSubjectMarks.length;
        nbParentSubjects++;
      } else {
        nbLoneSubjects++;
      }
    });
  });

  // Calculate dynamic spacing based on number of modules
  const totalModules = modules.length;
  let spacingBetweenModules;

  if (totalModules <= 2) {
    spacingBetweenModules = maxSpacing;
  } else if (totalModules >= 5) {
    spacingBetweenModules = minSpacing;
  } else {
    // Linear interpolation between max and min spacing
    spacingBetweenModules =
      maxSpacing - ((maxSpacing - minSpacing) * (totalModules - 2)) / 3;
  }

  // Add spacing to used height
  usedH += spacingBetweenModules * (totalModules - 1);

  const tableSize = 9.5;
  const smallerFont = false;
  return {
    availableHeight,
    usedH,
    nbLoneSubjects,
    NbSubSubjects,
    nbParentSubjects,
    tableSize,
    smallerFont,
    spacingBetweenModules, // Add this to the return object
  };
}

function getLogoWidth(imgData, fixedHeight = 14) {
  const originalWidth = imgData.width;
  const originalHeight = imgData.height;
  const aspectRatio = originalWidth / originalHeight;
  const autoWidth = fixedHeight * aspectRatio;
  return isNaN(autoWidth) || !isFinite(autoWidth) ? fixedHeight : autoWidth;
}
async function loadImage(src) {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img); // Resolve the promise when the image loads
    img.onerror = (err) => reject(err); // Reject if there's an error
  });
}

const roundRect = function (
  color,
  ctx,
  x,
  y,
  width,
  height,
  radius,
  fill,
  stroke
) {
  const cornerRadius = {
    upperLeft: 0,
    upperRight: 0,
    lowerLeft: 0,
    lowerRight: 0,
  };
  if (typeof stroke == "undefined") {
    stroke = true;
  }
  if (typeof radius === "object") {
    for (let side in radius) {
      cornerRadius[side] = radius[side];
    }
  }

  ctx.lineWidth = 1;
  ctx.strokeStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

  ctx.beginPath();
  ctx.moveTo(x + cornerRadius.upperLeft, y);
  ctx.lineTo(x + width - cornerRadius.upperRight, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + cornerRadius.upperRight);
  ctx.lineTo(x + width, y + height - cornerRadius.lowerRight);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - cornerRadius.lowerRight,
    y + height
  );
  ctx.lineTo(x + cornerRadius.lowerLeft, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - cornerRadius.lowerLeft);
  ctx.lineTo(x, y + cornerRadius.upperLeft);
  ctx.quadraticCurveTo(x, y, x + cornerRadius.upperLeft, y);
  ctx.closePath();
  if (stroke) {
    ctx.stroke();
  }
  if (fill) {
    ctx.fill();
  }
};

const componentToHex = function (c) {
  var hex = c.toString(16);
  return hex.length == 1 ? "0" + hex : hex;
};

const rgbToHex = function (r, g, b) {
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
};
function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
    ]
    : null;
}
const drawModuleHeader = function (
  doc,
  ctx,
  startY,
  title,
  bulletinColors,
  hideSubjectMax = false,
  hideMaxPerClass = false,
  hideMinPerClass = false,
  pedaLike = false,
  hideSubjectMark = false,
  hideModuleMark = false
) {
  // module title

  let x = 65;
  let y = startY;
  let width = 125;
  doc.setFillColor(...bulletinColors.main);
  if (pedaLike)
    roundRect(
      bulletinColors.main,
      ctx,
      x,
      y,
      width,
      8,
      { upperRight: 8 },
      true,
      true
    );
  else roundRect(bulletinColors.main, ctx, x, y, width, 8, {}, true, true);
  doc.setFillColor(0xff, 0xff, 0xff);
  doc.setDrawColor(0xff, 0xff, 0xff);
  doc.rect(x, y + 20, width + 10, 10, "FD");

  // module header shapes
  x = pedaLike ? 139 : 155;
  y = 9.6 + startY;
  width = pedaLike ? 63 : 47;
  doc.setFillColor(...bulletinColors.main);
  if (pedaLike)
    roundRect(
      bulletinColors.main,
      ctx,
      x,
      y,
      width,
      7.5,
      { upperRight: 7.5 },
      true,
      true
    );
  else roundRect(bulletinColors.main, ctx, x, y, width, 7.5, {}, true, true);
  // doc.setFillColor(0xff, 0xff, 0xff);
  // doc.setDrawColor(0xff, 0xff, 0xff);
  // doc.rect(x, y + 9.9, width + 10, 10, "FD");

  if (!hideSubjectMark) {
  x = 139;
  y = 9.6 + startY;
  width = 16;
  doc.setFillColor(...bulletinColors.secondary);
  //العدد / 20
  if (pedaLike)
    roundRect(
      bulletinColors.main,
      ctx,
      x,
      y,
      width,
      7.4,
      { upperRight: 7.4 },
      true,
      true
    );
  else roundRect(bulletinColors.main, ctx, x, y, width, 7.4, {}, true, true);
  }
  // doc.setFillColor(0xff, 0xff, 0xff);
  // doc.setDrawColor(0xff, 0xff, 0xff);
  // doc.rect(x, y + 9.9, width + 10, 10, "FD");
  doc.setLineWidth(0.4);
  doc.setFillColor(...bulletinColors.secondary);
  doc.setDrawColor(...bulletinColors.main);
  if (!hideModuleMark) {
  //معدل المجال;
    doc.rect(124 + (hideSubjectMark ? 16 : 0), 9.3 + startY, 15, 8, "FD");
  }
  //توصيّات المدّرس
  //rect(x: number, y: number, w: number, h: number, style?: string | null): JsPDF
  const minMaxPerClass = [hideMaxPerClass, hideMinPerClass].filter(
    (t) => t
  ).length;
  const appreciationX =
    minMaxPerClass == 0 ? 86 : minMaxPerClass == 1 ? 76 : 65;
  let appreciationW =
    38 + (minMaxPerClass == 0 ? 0 : minMaxPerClass == 1 ? 10 : 21);
  if (hideModuleMark) appreciationW += 15;
  if (hideSubjectMark) appreciationW += 16;
  doc.rect(appreciationX, 9.3 + startY, appreciationW, 8, "FD");
  //عدد بالقسم
  if (!hideMinPerClass) doc.rect(65, 9.3 + startY, 10, 8, "FD");
  if (!hideMaxPerClass)
    doc.rect(hideMinPerClass ? 65 : 75, 9.3 + startY, 10, 8, "FD");

  doc.line(155, 17.3 + startY, 202.5, 13.3 + startY);

  // header module texts
  if (/[a-zA-Z]/.test(title)) doc.setFont("Amiri", "Bold");
  else doc.setFont("Noto Sans Arabic", "Bold");
  doc.setFontSize(13);
  let text = title;
  doc.setTextColor(...bulletinColors.mainBorder);
  width = doc.getTextWidth(text);
  doc.text(text, 138 - width / 2 - 15, 6 + startY);

  doc.setFont("Amiri", "Bold");
  text = "المادة";
  width = doc.getTextWidth(text);
  doc.text(text, 179, 15 + startY);

  if (!hideSubjectMark) {
    text = "العدد / 20";
    if (hideSubjectMax) text = "العدد";
    doc.setFontSize(9);
    doc.setTextColor(...bulletinColors.main);
    width = doc.getTextWidth(text);
    doc.text(text, hideSubjectMax ? 145 : 141, 15 + startY);
  }

  if (!hideModuleMark) {
    text = "معدل\nالمجال";
    doc.setFontSize(9);
    doc.setTextColor(...bulletinColors.main);
    width = doc.getTextWidth(text);
    const moduleMarkX = 132 + (hideSubjectMark ? 15 : 0);
    doc.text(text, moduleMarkX, 12.2 + startY, "center");
  }
  let appreciationTextX =
    minMaxPerClass == 0 ? 94 : minMaxPerClass == 1 ? 83 : 73;
  if (hideModuleMark) appreciationTextX += 15;
  if (hideSubjectMark) appreciationTextX += 16;
  text = "ملاحظات المدّرس";
  doc.setFontSize(11);
  doc.setTextColor(...bulletinColors.main);
  width = doc.getTextWidth(text);
  doc.text(text, appreciationTextX, 14.5 + startY);

  if (!hideMaxPerClass) {
    text = "أعلى\nعدد بالقسم";
    doc.setFontSize(6);
    doc.setTextColor(...bulletinColors.main);
    width = doc.getTextWidth(text);
    doc.text(text, hideMinPerClass ? 70 : 80, 12 + startY, { align: "center" });
  }
  if (!hideMinPerClass) {
    text = "أدنى\nعدد بالقسم";
    doc.setFontSize(6);
    doc.setTextColor(...bulletinColors.main);
    width = doc.getTextWidth(text);
    doc.text(text, 70, 12 + startY, { align: "center" });
  }
};
const getCertificationNameByMoyenne = function (moyenne, certifs) {
  for (let cert of certifs)
    if (
      Number(moyenne) >= Number(cert.min) &&
      Number(moyenne) <= Number(cert.max)
    ) {
      return cert.name;
    }

  return " ";
};
const fuseSubSubjects = (student) => ({
  ...student,
  modules: student.modules.map((m) => ({
    ...m,
    subjects: m.subjects.map((s) => {
      if (s.subSubjectMarks) delete s.subSubjectMarks;
      return s;
    }),
  })),
});
function calculateOptimalFontSize(text, maxWidth, minSize = 8, maxSize = 12) {
  if (!text) return maxSize;
  const doc = new JsPDF();
  let fontSize = maxSize;
  doc.setFontSize(fontSize);

  while (doc.getTextWidth(text) > maxWidth && fontSize > minSize) {
    fontSize -= 0.5;
    doc.setFontSize(fontSize);
  }

  return fontSize;
}

const arrayDetails = {
  colSpans: {
    subject: (hideSubject, nbSubSubjects) => {
      if (hideSubject) return nbSubSubjects > 0 ? 0 : 3;
      else return nbSubSubjects > 0 ? 1 : 3;
    },
    subSubject: (hideSubject) => (hideSubject ? 3 : 1),
  },
  rowSpans: {
    subject: (hideSubject, nbSubSubjects) => {
      if (hideSubject) return 1;
      else return nbSubSubjects > 0 ? nbSubSubjects : 1;
    },
    subSubject: 1,
  },
  coldIdxs: {
    subject: (hidenCols) => {
      let idx = 8;
      if (hidenCols == 2) idx = 5;
      else if (hidenCols == 1) idx = 7;
      return idx
    },
    subjectMark: (hidenCols, hasSubmodulesMoyEnabled = false, firstSubmodule = false) => {
      let idx = 5;
      if (hidenCols == 2) idx = 2;
      else if (hidenCols == 1) idx = 4;
      return idx + (hasSubmodulesMoyEnabled && !firstSubmodule ? 1 : 0);
    },
    subSubject: (hidenCols, hideSubject) => {
      if (hidenCols == 2) return hideSubject ? 5 : 4;
      else if (hidenCols == 1) return 6; //hideSubject?7:6;
      return 7; //hideSubject?8:7;
    },
    subSubjectMark: (hidenCols, hideSubject) => {
      if (hidenCols == 2) return hideSubject ? 2 : 3;
      else if (hidenCols == 1) return 5; //hideSubject?4:5;
      return 6; //hideSubject?5:6;
    },
    moduleMark: (hidenCols) => {
      if (hidenCols == 2) return 1;
      else if (hidenCols == 1) return 3;
      return 4;
    },
    appreciation: (hidenCols) => {
      if (hidenCols == 2) return 0;
      else if (hidenCols == 1) return 2;
      return 3;
    },
    spaceAfterMinOrMax: (hidenCols) => (hidenCols == 0 ? 2 : 1),
  },
};
/**
 * draw side function, used to draw the side part of bulletin
 * @param {Bulletin} bulletin - Bulletin object

 */
function getModuleBodyAutoTable(
  bulletin,
  tempPDF,
  tempCtx,
  modules,
  spacingBetweenModules,
  factorCoef,
  factorAddition,
  sourceY,
  continueLoop,
  firstEntry
) {
  const hideSubject = bulletin.body.hideSubjects;
  const bulletinColors = bulletin.colorPalette;
  const { showSubmodule, showSubmoduleMoy, hideMaxPerClass, hideMinPerClass, hideSubjectMark, hideModuleMark } = bulletin.body;
  const numberOfHiddenMinMax = [hideMaxPerClass, hideMinPerClass].filter(
    (h) => h
  ).length;
  const cellToDelete = {};
  const pedaLike = bulletin.pedaLike;
  let factor = 1 + factorAddition;
  let currentY = sourceY;
  let afterModuleY, beforeModuleY;
  modules.forEach((m) => {
    beforeModuleY = currentY;
    afterModuleY = currentY;
    drawModuleHeader(
      tempPDF,
      tempCtx,
      currentY,
      m.name,
      bulletinColors,
      hideSubject,
      hideMaxPerClass,
      hideMinPerClass,
      pedaLike,
      hideSubjectMark,
      hideModuleMark
    );
    currentY += 17.2;
    currentY += 2;
    let arr = [];
    let array = [];
    let moduleSubjectFontSize = 12; // Default font size for the module
    let currentSize = 12;
    let currentColor = "#000000";
    let longNameSubject = false;

    // Submodule name and moyenne sections
    const hasSubmodules = m.submodules && m.submodules.length > 0;
    const moduleHasSubmoduleNameEnabled = showSubmodule && hasSubmodules;
    const moduleHasSubmoduleMoyEnabled = showSubmoduleMoy && hasSubmodules;
    const firstSubmodule = (moduleId) => moduleId ? m.submodules.findIndex((submodule) => submodule._id == moduleId) == 0 : false;

    m.subjects.forEach((c) => {
      // submodule name section
      let subFormattedName = c.name;
      if (c.name.length > 20 && c.name.indexOf(" و ") != -1) {
        let indexOfFirst = c.name.indexOf(" و ");
        subFormattedName =
          c.name.slice(0, indexOfFirst) + "\n" + c.name.slice(indexOfFirst);
      }
      if (c.length > 22) longNameSubject = true;
      moduleSubjectFontSize = calculateOptimalFontSize(
        subFormattedName,
        23, // max width for subject name column
        10, // min size - increased minimum size
        12 // max size
      );
      moduleSubjectFontSize *= factor;

      const nbOfSubSubject = c.subSubjectMarks ? c.subSubjectMarks.length : 0;

      const subjectPositions = {
        idx: arrayDetails.coldIdxs.subject(numberOfHiddenMinMax, moduleHasSubmoduleMoyEnabled, firstSubmodule(c.submoduleID)),
        colSpan: arrayDetails.colSpans.subject(hideSubject, nbOfSubSubject),
        rowSpan: arrayDetails.rowSpans.subject(hideSubject, nbOfSubSubject),
      };

      const subjectMarkPosition =
        arrayDetails.coldIdxs.subjectMark(numberOfHiddenMinMax);

      const hideSubjectAndNoSubjects =
        !hideSubject ||
        (hideSubject && (!c.subSubjectMarks || c.subSubjectMarks.length == 0));

      if (hideSubjectAndNoSubjects) {
        arr[subjectPositions.idx] = {
          content: subFormattedName,
          rowSpan: subjectPositions.rowSpan,
          colSpan: subjectPositions.colSpan,
          styles: {
            halign: "right",
            valign: "middle",
            fillColor: rgbToHex(...bulletinColors.main),
            fontSize: moduleSubjectFontSize,
            textColor: "#ffffff",
            lineColor: rgbToHex(...bulletinColors.secondary),
            cellPadding: {
              top: 0.8,
              bottom: 0.8,
              right: 2,
              left: 1,
            },
          },
        };
      }

      // ﺍﻟﻌﺪﺩ / 20
      if (hideSubjectAndNoSubjects && !bulletin.body.hideSubjectMark) {
        if (bulletin.body.subjectMark?.auto === false) {
          [currentSize, currentColor] = speceficStyle(
            bulletin.body.subjectMark
          );
        }
        if (!currentSize) currentSize = 11;
        if (!currentColor) currentColor = "#000000";
        arr[subjectMarkPosition] = {
          content: c.mark ?? "--",
          rowSpan: subjectPositions.rowSpan,
          styles: {
            halign: "center",
            valign: "middle",
            fontSize: currentSize * factor,
            textColor: currentColor,
            lineColor: [...bulletinColors.main],
            cellPadding: 0,
          },
        };
        [currentSize, currentColor] = [false, false];
      }

      // arr[4] = cellToDelete;
      // arr[3] = cellToDelete;
      // arr[2] = cellToDelete;

      //best mark
      //? use min from subject if hide subject and no subsubject else use subsubject one
      if (!hideMaxPerClass && hideSubjectAndNoSubjects) {
        if (bulletin.body.maxPerClass?.auto === false) {
          [currentSize, currentColor] = speceficStyle(
            bulletin.body.maxPerClass
          );
        }
        if (!currentSize) currentSize = 10;
        if (!currentColor) currentColor = "#000000";
        arr[hideMinPerClass ? 0 : 1] = {
          content: c.max,
          rowSpan: subjectPositions.rowSpan,
          styles: {
            halign: "center",
            valign: "middle",
            fontSize: currentSize * factor,
            textColor: currentColor,
            cellPadding: 0,
            lineColor: [...bulletinColors.main],
          },
        };
        [currentSize, currentColor] = [false, false];
      }
      //lowest mark
      //? use min from subject if hide subject and no subsubject else use subsubject one
      if (!hideMinPerClass && hideSubjectAndNoSubjects) {
        if (bulletin.body.minPerClass?.auto === false) {
          [currentSize, currentColor] = speceficStyle(
            bulletin.body.minPerClass
          );
        }
        if (!currentSize) currentSize = 10;
        if (!currentColor) currentColor = "#000000";
        arr[0] = {
          content: c.min,
          rowSpan: subjectPositions.rowSpan,
          styles: {
            halign: "center",
            valign: "middle",
            fontSize: currentSize * factor,
            textColor: currentColor,
            cellPadding: 0,
            lineColor: [...bulletinColors.main],
          },
        };
        [currentSize, currentColor] = [false, false];
      }
      // sub subject
      if (c.subSubjectMarks) {
        c.subSubjectMarks.forEach((subs) => {
          try {
            const subSubjectPositions = {
              idx: arrayDetails.coldIdxs.subSubject(
                numberOfHiddenMinMax,
                hideSubject
              ),
              colSpan: arrayDetails.colSpans.subSubject(hideSubject),
            };
            const subSubjectMarkPosition = arrayDetails.coldIdxs.subSubjectMark(
              numberOfHiddenMinMax,
              hideSubject
            );

            if (bulletin.body.subsubjectMark?.auto === false) {
              [currentSize, currentColor] = speceficStyle(
                bulletin.body.subsubjectMark
              );
            }
            if (!currentSize) currentSize = hideSubject ? 11 : 9;
            if (!currentColor) currentColor = "#000000";
            if (!bulletin.body.hideSubjectMark) {
            arr[subSubjectMarkPosition] = {
              content: subs.mark,
              styles: {
                halign: "center",
                valign: "middle",
                cellPadding: 0,
                fontSize: currentSize * factor,
                textColor: currentColor,
                fillColor: hideSubject
                  ? "#ffffff"
                  : rgbToHex(...bulletinColors.secondary),
                lineColor: [...bulletinColors.main],
              },
            };
            }
            [currentSize, currentColor] = [false, false];

            // Format sub-subject name with proper handling of diacritical marks
            const ssName = formatArabicText(subs.name, subs.maxMark);

            // Calculate optimal font size for sub-subject name
            const calculateOptimalFontSizeParms = hideSubject
              ? [
                ssName,
                23, // max width for subject name column
                10, // min size - increased minimum size
                12, // max size
              ]
              : [
                ssName,
                18, // max width for sub-subject column
                8, // min size
                10, // max size
              ];
            const ssFontSize = calculateOptimalFontSize(
              ...calculateOptimalFontSizeParms
            );

            arr[subSubjectPositions.idx] = {
              content: ssName,
              colSpan: subSubjectPositions.colSpan + (bulletin.body.hideSubjectMark ? 1 : 0),
              styles: {
                halign: "right",
                valign: "middle",
                cellPadding: 0.5,
                fontSize: ssFontSize * factor,
                fillColor: hideSubject
                  ? rgbToHex(...bulletinColors.main)
                  : rgbToHex(...bulletinColors.secondary),
                textColor: hideSubject
                  ? rgbToHex(...bulletinColors.mainBorder)
                  : rgbToHex(...bulletinColors.main),
                lineColor: hideSubject
                  ? rgbToHex(...bulletinColors.mainBorder)
                  : bulletinColors.main,
              },
            };
            if (hideSubject) {
              if (!hideMaxPerClass) {
                if (bulletin.body.maxPerClass?.auto === false) {
                  [currentSize, currentColor] = speceficStyle(
                    bulletin.body.maxPerClass
                  );
                }
                if (!currentSize) currentSize = 10;
                if (!currentColor) currentColor = "#000000";
                arr[hideMinPerClass ? 0 : 1] = {
                  content: subs.max,
                  styles: {
                    halign: "center",
                    valign: "middle",
                    fontSize: currentSize * factor,
                    textColor: currentColor,
                    cellPadding: 0,
                    lineColor: [...bulletinColors.main],
                  },
                };
                [currentSize, currentColor] = [false, false];
              }
              if (!hideMinPerClass) {
                if (bulletin.body.minPerClass?.auto === false) {
                  [currentSize, currentColor] = speceficStyle(
                    bulletin.body.minPerClass
                  );
                }
                if (!currentSize) currentSize = 10;
                if (!currentColor) currentColor = "#000000";
                arr[0] = {
                  content: subs.min,
                  styles: {
                    halign: "center",
                    valign: "middle",
                    fontSize: currentSize * factor,
                    textColor: currentColor,
                    cellPadding: 0,
                    lineColor: [...bulletinColors.main],
                  },
                };
                [currentSize, currentColor] = [false, false];
              }
            }
            array.push(arr);
            arr = [];
          } catch (error) {
            console.error("Error processing sub-subject:", error);
          }
        });
      } else array.push(arr);
      arr = [];
      currentColor = "#000000";
    });

    const appreciationPos =
      arrayDetails.coldIdxs.appreciation(numberOfHiddenMinMax);
    const moduleMarkPos =
      arrayDetails.coldIdxs.moduleMark(numberOfHiddenMinMax);

    // submodule name and moyenne section
    if (moduleHasSubmoduleNameEnabled || moduleHasSubmoduleMoyEnabled) {

      // show submodule moyenne second

      let subModuleMoyRowSpan = 1
      let arrayIndex = 0;
      for (let i = 0; i < m.submodules.length; i++) {
        const submodule = m.submodules[i];
        const submoduleSubjects = m.subjects.filter((subject) => subject.submoduleID === submodule._id);
        subModuleMoyRowSpan = submoduleSubjects.reduce((acc, subject) => acc + (subject.subSubjectMarks ? subject.subSubjectMarks.length : 1), 0);
        const subModuleMarkPosition = {
          idx: numberOfHiddenMinMax == 2 ? 1 : (numberOfHiddenMinMax == 1 ? 3 : 4),
          colSpan: 1,
          rowSpan: subModuleMoyRowSpan,
        }
        // submodule Name first
        if (moduleHasSubmoduleNameEnabled) {
          const submoduleNameArray = []
          //! important: create empty cells for max and gap
          if (!hideMaxPerClass)
            submoduleNameArray.push({
              content: "",
              rowSpan: 1,
              colSpan: 1,
              styles: {
                halign: "center",
                valign: "middle",
                fillColor: "#ffffff", //"#40bbef",
                fontSize: moduleSubjectFontSize + 1,
                cellPadding: 0,
                textColor: "#40bbef",
                lineColor: [...bulletinColors.main],
              },
            });
          //! important: create empty cells for min and gap
          if (!hideMinPerClass)
            submoduleNameArray.push({
              content: "",
              rowSpan: 1,
              colSpan: 1,
              styles: {
                halign: "center",
                valign: "middle",
                fillColor: "#ffffff", //"#40bbef",
                fontSize: moduleSubjectFontSize + 1,
                cellPadding: 0,
                textColor: "#40bbef",
                lineColor: [...bulletinColors.main],
              },
            });
          //! important: gap between min-max and appreciation
          if ((!hideMaxPerClass || !hideMinPerClass) && i == 0) {
            submoduleNameArray.push({
              content: "",
              rowSpan: array.length + m.submodules.length,
              colSpan: 1,
              styles: {
                halign: "center",
                valign: "middle",
                fillColor: "#ffffff", //"#40bbef",
                fontSize: moduleSubjectFontSize + 1,
                cellPadding: 0.5,
                textColor: "#40bbef",
                lineColor: [52, 172, 215],
              },
            });
          }
          //! important: appreciation cell - to be overwritten by the appreciation logic later
          submoduleNameArray.push({ content: "", rowSpan: 1, colSpan: 1 })
          // important: moyenne cell - to be overwritten by the moyenne if !moduleHasSubmoduleMoyEnabled
          if (!bulletin.body.hideModuleMark && !moduleHasSubmoduleMoyEnabled && i == 0) submoduleNameArray.push({ content: "", rowSpan: 1, colSpan: 1 })
          let subModuleNameColSpan = 4
          if (!bulletin.body.hideModuleMark && moduleHasSubmoduleMoyEnabled) subModuleNameColSpan += 1;
          if (bulletin.body.hideSubjectMark) subModuleNameColSpan -= 1;
          submoduleNameArray.push({
            content: submodule.label,
            rowSpan: 1,
            colSpan: subModuleNameColSpan,
            styles: {
              halign: "center",
              valign: "middle",
              fillColor: "#ffffff", //"#40bbef",
              fontSize: moduleSubjectFontSize + 1,
              cellPadding: 0.5,
              textColor: [...bulletinColors.main],
              lineColor: [...bulletinColors.main],
              overflow: "visible",
            },
          })
          // order important for the next two lines
          array.splice(i == 0 ? 0 : arrayIndex, 0, submoduleNameArray);
          arrayIndex++;
        }
        if (moduleHasSubmoduleMoyEnabled && !bulletin.body.hideModuleMark) {
          if (bulletin.body.moduleMark?.auto === false) {
            [currentSize, currentColor] = speceficStyle(bulletin.body.moduleMark);
          }
          if (!currentSize) currentSize = 10;
          if (!currentColor) currentColor = "#000000";          
          array[arrayIndex][subModuleMarkPosition.idx] = {
            content: submodule.moyenne,
            rowSpan: subModuleMarkPosition.rowSpan,
            colSpan: subModuleMarkPosition.colSpan,
            styles: {
              halign: "center",
              fontSize: currentSize * factor,
              textColor: currentColor,
              valign: "middle",
              lineColor: [...bulletinColors.main],
            },
          }
        }
        arrayIndex += subModuleMoyRowSpan;
      }

    }
    if (!moduleHasSubmoduleMoyEnabled && !bulletin.body.hideModuleMark) {
      //ﻣﻌﺪﻝ ﺍﻟﻤﺠﺎﻝ
      if (bulletin.body.moduleMark?.auto === false) {
        [currentSize, currentColor] = speceficStyle(bulletin.body.moduleMark);
      }
      if (!currentSize) currentSize = 10;
      if (!currentColor) currentColor = "#000000";      
      array[0][moduleMarkPos] = {
        content: m.moyenne,
        rowSpan: array.length,
        colSpan: 1,
        styles: {
          halign: "center",
          fontSize: currentSize * factor,
          textColor: currentColor,
          valign: "middle",
          lineColor: [...bulletinColors.main],
        },
      };
    }
    // gad between min-max and appreciation
    if ((!hideMaxPerClass || !hideMinPerClass) && !moduleHasSubmoduleNameEnabled) {
      array[0][arrayDetails.coldIdxs.spaceAfterMinOrMax(numberOfHiddenMinMax)] =
      {
        content: "",
        rowSpan: array.length,
        styles: {
          lineColor: [...bulletinColors.main],
        },
      };
    }
    //appreciation
    // use module appreciation if:
    // - no submodule or there's submodules but no subject
    // - no appreciation and module has appreciation
    if (
      !m.submodules ||
      m.submodules.every(
        (submodule) =>
          submodule.NbSubjects === 0 ||
          ((!submodule.appreciation || !submodule?.appreciation?.length == 0) &&
            m.appreciation &&
            m.appreciation.length > 0)
      )
    ) {
      const appreciationText = m.appreciation.trim();
      if (bulletin.body.appreciation?.auto === false) {
        [currentSize, currentColor] = speceficStyle(bulletin.body.appreciation);
      }
      if (!currentColor) currentColor = "#000000";
      if (!currentSize)
        currentSize = calculateOptimalFontSize(
          appreciationText,
          38, // max width for appreciation column
          9, // min size - increased minimum size
          11 // max size - adjusted for better readability
        );

      array[0][appreciationPos] = {
        content: appreciationText,
        rowSpan: array.length,
        colSpan: 1 + (bulletin.body.hideSubjectMark ? 1 : 0) + (bulletin.body.hideModuleMark ? 1 : 0),
        styles: {
          halign: "center",
          valign: "middle",
          fontSize: currentSize * factor,
          textColor: currentColor,
          lineColor: [...bulletinColors.main],
          cellPadding: 1,
        },
      };
      [currentSize, currentColor] = [false, false];
    } else {
      let appIndex = 0;
      m.submodules.forEach((subModule) => {
        const subSubjectCount = m.subjects
          .filter((subject) => subject.submoduleID === subModule._id)
          .reduce(
            (acc, subject) =>
              acc +
              (subject.subSubjectMarks ? subject.subSubjectMarks.length : 1),
            0
          );
        let rowNumber = subSubjectCount + (moduleHasSubmoduleNameEnabled ? 1 : 0);
        const appreciationText = subModule.appreciation.trim();
        if (bulletin.body.appreciation?.auto === false) {
          [currentSize, currentColor] = speceficStyle(
            bulletin.body.appreciation
          );
        }
        if (!currentColor) currentColor = "#000000";
        if (!currentSize)
          currentSize = calculateOptimalFontSize(
            appreciationText,
            38, // max width for appreciation column
            9, // min size
            11 // max size
          );

        const shouldAdjustPos = appIndex !== 0 && moduleHasSubmoduleNameEnabled && numberOfHiddenMinMax !== 2;
        array[appIndex][appreciationPos - (shouldAdjustPos ? 1 : 0)] = {
          content: appreciationText,
          rowSpan: rowNumber,
          colSpan: 1 + (bulletin.body.hideSubjectMark ? 1 : 0) + (bulletin.body.hideModuleMark ? 1 : 0),
          styles: {
            cellPadding: 1,
            halign: "center",
            valign: "middle",
            fontSize: currentSize * factor,
            textColor: currentColor,
            lineColor: [...bulletinColors.main],
          },
        };
        [currentSize, currentColor] = [false, false];
        appIndex += rowNumber
      });
    }

    // if module submodule Moy is enabled and peda mode is enabled, add module moyenne to the array in peda mode
    if (bulletin.body.pedaMoyLike) {
      const moduleMoyRow = [
        {
          content: "",
          rowSpan: 1,
          colSpan: numberOfHiddenMinMax == 2 ? 0 : (numberOfHiddenMinMax == 1 ? 2 : 3),
          styles: {
            cellMarging: 2,
            fillColor: "#ffffff",
            lineColor: {
              top: [...bulletinColors.main],
              bottom: "#ffffff",
              left: "#ffffff",
              right: "#ffffff",
            }
          },
        },
        {
          content: m.moyenne,
          rowSpan: 1,
          colSpan: 1,
          styles: {
            cellMarging: 2,
            halign: "center",
            valign: "middle",
            fontSize: 14,
            textColor: [...bulletinColors.main],
            fillColor: [...bulletinColors.mainBorder],
            lineColor: [...bulletinColors.main]
          },
        },
        {
          content: "معدل المجال",
          rowSpan: 1,
          colSpan: Math.max(...m.subjects.map((subject) => arrayDetails.colSpans.subject(hideSubject, subject.subSubjectMarks ? subject.subSubjectMarks.length : 0)), 1) + 2,
          styles: {
            cellMarging: 2,
            halign: "center",
            valign: "middle",
            fontSize: 14,
            textColor: [...bulletinColors.mainBorder],
            fillColor: [...bulletinColors.main],
            lineColor: [...bulletinColors.mainBorder]
          }
        },
      ]
      array.push(moduleMoyRow)

    }



    for (let row = 0; row < array.length; row++) {
      array[row] = array[row].filter((cell) => cell !== cellToDelete);
    }

    const bodyStyles =
      moduleSubjectFontSize > 12
        ? {
          minCellHeight: 5,
          cellPadding: { vertical: 1, horizontal: 1 },
        }
        : {};
    let columnStyles = {};
    let columWidths = {
      minSubjectPerClass: 10,
      maxSubjectPerClass: 10,
      gapBetweenSubjectMinMaxAndAppreciation: 1,
      moduleAverage: 15,
      subjectAverage: 16,
      subsubjectAverage: 8,
      subsubjectName: 18,
      subjectName: longNameSubject ? 25 : 23,
      appreciation: function () {
        // don't count appreciation itself
        const values = [
          this.minSubjectPerClass,
          this.maxSubjectPerClass,
          this.gapBetweenSubjectMinMaxAndAppreciation,
          this.moduleAverage,
          this.subjectAverage,
          this.subsubjectAverage,
          this.subsubjectName,
          (longNameSubject ? 25 : 23)
        ];
        let appxWidth = (longNameSubject ? 141 : 139) - values.reduce((a, b) => a + b, 0);
        //if (bulletin.body.hideSubjectMark) appxWidth += 16;
        //if (bulletin.body.hideModuleMark) appxWidth += 16;
        return appxWidth;
      },
    }
    let columnsOrder = [
      'minSubjectPerClass', 'maxSubjectPerClass', 'gapBetweenSubjectMinMaxAndAppreciation', 'appreciation', 'moduleAverage', 'subjectAverage', 'subsubjectAverage', 'subsubjectName', 'subjectName'
    ]
    // numberOfHiddenMinMax == 2, zero min, max and gap
    if (numberOfHiddenMinMax == 2) columWidths = { ...columWidths, minSubjectPerClass: 0, maxSubjectPerClass: 0, gapBetweenSubjectMinMaxAndAppreciation: 0 }
    else if (numberOfHiddenMinMax == 1) columWidths = { ...columWidths, minSubjectPerClass: 0 }
    // filter out the columns who are zeroed
    columnsOrder = columnsOrder.filter(columnName => columnName == "appreciation" ? columWidths.appreciation() : columWidths[columnName])
    for (let i = 0; i < columnsOrder.length; i++) {
      if (columnsOrder[i] == "appreciation") {
        columnStyles[i] = { cellWidth: columWidths.appreciation() }
      } else {
        columnStyles[i] = { cellWidth: columWidths[columnsOrder[i]] }
      }
    }
    const autoTable = {
      startY: currentY,
      theme: "grid",
      styles: {
        font: "Amiri",
        halign: "center",
        fontStyle: "Bold",
      },
      bodyStyles: bodyStyles,
      cellPadding: 0,
      body: array,
      margin: { left: 65, bottom: 0 },
      columnStyles: columnStyles,
    };
    tempPDF.autoTable(autoTable);
    afterModuleY = tempPDF.lastAutoTable.finalY;
    continueLoop =
      beforeModuleY > afterModuleY ||
      afterModuleY >= bulletin.pdf_height - (bulletin.spacing.bottomLimit || 0);

    // Add dynamic spacing after each module except the last one
    currentY =
      tempPDF.lastAutoTable.finalY +
      spacingBetweenModules * (factor * (factorCoef === 1 ? 2 : 1));
  });
  if (!firstEntry && continueLoop) factorAddition += factorCoef * 0.05;
  return {
    tempPDF,
    tempCtx,
    factorAddition,
    continueLoop,
  };
}

class Bulletin {
  constructor(
    type = "MS",
    colorPalette = {
      main: "#40bbef",
      mainBorder: "#f0f5f7",
      secondary: "#e0edf3",
      secondaryBorder: "#40bbef",
      tertiary: "#e1e1e2",
      tertiaryBorder: "#40bbef",
      normal: {
        moy: "#433e3f",
        rank: "#433e3f",
        min: "#433e3f",
        max: "#433e3f",
      },
      peda: {
        moy: "#433e3f",
        rank: "#433e3f",
        min: "#433e3f",
        max: "#433e3f",
      },
      personalized: {
        moy: "#433e3f",
        rank: "#433e3f",
        min: "#433e3f",
        max: "#433e3f",
      },
    },
    bg_image = null,
    qrCode = null,
    pedaLike = false,
    headers = {
      schoolName: true,
      birthDate: true,
      schoolarYear: true,
      className: true,
      nbInscription: true,
      uniqueIden: true,
      govText: false,
      studentName: true,
      schoolLogo: null,
      studentImage: null,
      govLogo: null,
    },
    body = {
      fuseSubjects: false,
      hideSubjects: false,
      hideMinPerClass: false,
      hideMaxPerClass: false,
      hideSubjectMark: false,
      hideModuleMark: false,
    },
    side = {
      stamp: null,
      printDate: false,
      normal: {
        allow: true,
        min: true,
        max: true,
        rank: true,
      },
      peda: {
        allow: true,
        min: true,
        max: true,
        rank: true,
      },
      personalized: {
        allow: true,
        min: true,
        max: true,
        rank: true,
      },
      certif: true,
      adminAppreciation: false,
    },
    spacing = {
      forceOnePage: false,
      stretchFullPage: false,
      squeezeToNLines: 0,
      margin: 12,
      bottomLimit: 5,
      trimersterLineMargin: 0,
    },
    options = {
      logosPosition: {
        //"c": center, "l": left, "c": center
        schoolLogo: ["c", 0],
        //studentImage: ["c", 0],
        //govLogo: ["c", 0],
      },
      headerFonts: {
        "*": {
          title: {
            font: "Amiri",
            style: "Bold",
            color: "#000000",
            size: 12,
          },
          value: {
            font: "Amiri",
            style: "normal",
            color: "#000000",
            size: 12,
          },
        },
      },
      headersPositions: {
        //"c": center, "l": left, "c": center, "b": bellow the trimester line,
        schoolName: ["r", 0],
        studentName: ["r", 1],
        birthDate: ["r", 2],
        uniqueIden: ["r", 3],
        schoolarYear: ["l", 0],
        className: ["l", 1],
        nbInscription: ["l", 2],
      },
    },
    preview_mode = false
  ) {
    this.type = type;
    this.preview_mode = preview_mode;
    this.colorPalette = colorPalette;
    this.pedaLike = pedaLike;
    this.bg_image = bg_image;
    this.qrCode = qrCode;
    this.headers = headers;
    this.body = body;
    this.side = side;
    this.spacing = {
      ...{
        forceOnePage: false,
        stretchFullPage: false,
        squeezeToNLines: 0,
        margin: 12,
        bottomLimit: 5,
        trimersterLineMargin: 0,
      },
      ...spacing,
    };
    this.options = options;
    this.doc = new JsPDF();
    this.ctx = this.doc.context2d;
    this.pdf_width = this.doc.internal.pageSize.width;
    this.pdf_height = this.doc.internal.pageSize.height;
    this.X = 0;
    this.Y = spacing.margin;
    this.currentStudent = 1;
    this.onTwoPages = false;
    for (let [color, val] of Object.entries(this.colorPalette)) {
      if (["normal", "peda", "personalized"].includes(color)) {
        this.colorPalette[color] = {
          moy:
            typeof this.colorPalette[color].moy === "string"
              ? hexToRgb(this.colorPalette[color].moy)
              : this.colorPalette[color].moy,
          rank:
            typeof this.colorPalette[color].rank === "string"
              ? hexToRgb(this.colorPalette[color].rank)
              : this.colorPalette[color].rank,
          min:
            typeof this.colorPalette[color].min === "string"
              ? hexToRgb(this.colorPalette[color].min)
              : this.colorPalette[color].min,
          max:
            typeof this.colorPalette[color].max === "string"
              ? hexToRgb(this.colorPalette[color].max)
              : this.colorPalette[color].max,
        };
      } else
        this.colorPalette[color] =
          typeof val === "string" ? hexToRgb(val) : val;
    }
    for (let [header, fontData] of Object.entries(this.options.headerFonts)) {
      this.options.headerFonts[header].title.color =
        typeof fontData.title.color === "string"
          ? hexToRgb(fontData.title.color)
          : fontData.title.color;
      this.options.headerFonts[header].value.color =
        typeof fontData.value.color === "string"
          ? hexToRgb(fontData.value.color)
          : fontData.value.color;
    }
    this.factorAddition = 0;
    this.factorAdditionMutated = false;
  }
  async loadImages(baseUrl = null) {
    // loading images
    let imgsPromises = [];
    imgsPromises.push(this.bg_image);
    imgsPromises.push(this.headers.schoolLogo);
    imgsPromises.push(this.headers.govLogo);
    imgsPromises.push(this.headers.studentImage);
    imgsPromises.push(this.side.stamp);
    imgsPromises.push(this.qrCode);
    imgsPromises = imgsPromises.map((imgSrc) =>
      imageUrlToBase64(baseUrl ? baseUrl + imgSrc : imgSrc)
    );
    const [bg_image, schoolLogo, govLogo, studentImage, stamp, qrCode] =
      await Promise.allSettled(imgsPromises);

    this.bg_image = bg_image.status === "fulfilled" ? bg_image.value : false;
    this.headers.schoolLogo =
      schoolLogo.status === "fulfilled" ? schoolLogo.value : false;
    this.headers.govLogo =
      govLogo.status === "fulfilled" ? govLogo.value : false;
    this.headers.studentImage =
      studentImage.status === "fulfilled" ? studentImage.value : false;
    this.side.stamp = stamp.status === "fulfilled" ? stamp.value : false;
    this.qrCode = qrCode.status === "fulfilled" ? qrCode.value : false;

    if (this.bg_image)
      this.doc.addImage(
        this.bg_image,
        "JPEG",
        0,
        0,
        this.pdf_width,
        this.pdf_height
      );
  }
  async reloadStudentsImage(settings, studentsData) {
    const resolver = studentsData.map((student) => {
      return new Promise(async (resolve, _) => {
        let loadedImage = null;
        const studentImage = student.photo;
        if (!studentImage) {
          resolve({
            id: student._id,
            photo: null,
          });
        } else {
          try {
            loadedImage = await imageUrlToBase64(
              settings.resourcesUrl.replace("FILE_PATH", studentImage)
            );
            resolve({
              id: student._id,
              photo: loadedImage,
            });
          } catch (error) {
            resolve({
              id: student._id,
              photo: null,
            });
          }
        }
      });
    });
    return await Promise.all(resolver);
  }
  setBG() {
    if (this.bg_image)
      this.doc.addImage(
        this.bg_image,
        "JPEG",
        0,
        0,
        this.pdf_width,
        this.pdf_height
      );
  }
}

/**
 * draw header function, used to draw the top part of bulletin
 * @param {Bulletin} bulletin - Bulletin object
 * @param {
 * {
 *    schoolName:String
 *    studentName:String
 *    studentPhoto:String|undefined
 *    birthDate:String
 *    uniqueIden:String
 *    schoolarYear:String
 *    className:String
 *    nbInscription:String
 *    selectedTrimester:String
 *    gender:String
 *    selectedLevel:Number
 *    schoolDetails:Object
 * }
 * } data
 */
async function drawHeader(bulletin, data) {
  const pdf_width = bulletin.pdf_width;
  const doc = bulletin.doc;
  bulletin.Y = 12;
  let headerLY = 12;
  let headerCY = 12;
  let headerRY = 12;
  // getting headers fonts
  const headerFonts = bulletin.options.headerFonts;
  const defaultHeaderFont = Object.keys(headerFonts).includes("*")
    ? headerFonts["*"]
    : {
      title: {
        font: "Amiri",
        style: "normal",
        color: "#000000",
        size: 12,
      },
      lineReturn: false,
      value: {
        font: "Amiri",
        style: "normal",
        color: "#000000",
        size: 12,
      },
    };
  const topHeaders = [];
  const bottomHeaders = [];
  const headersFonts = {
    schoolName: headerFonts.schoolName
      ? headerFonts.schoolName
      : defaultHeaderFont,
    studentName: headerFonts.studentName
      ? headerFonts.studentName
      : defaultHeaderFont,
    birthDate: headerFonts.birthDate
      ? headerFonts.birthDate
      : defaultHeaderFont,
    uniqueIden: headerFonts.uniqueIden
      ? headerFonts.uniqueIden
      : defaultHeaderFont,
    schoolarYear: headerFonts.schoolarYear
      ? headerFonts.schoolarYear
      : defaultHeaderFont,
    className: headerFonts.className
      ? headerFonts.className
      : defaultHeaderFont,
    nbInscription: headerFonts.nbInscription
      ? headerFonts.nbInscription
      : defaultHeaderFont,
  };
  // getting headers poisition
  for (let [header, position] of Object.entries(
    bulletin.options.headersPositions
  )) {
    if (bulletin.headers[header]) {
      if (position[0] == "b")
        bottomHeaders.push({
          postition: position[1],
          font: headersFonts[header],
          key: header,
        });
      else
        topHeaders.push({
          orientation: position[0],
          postition: position[1],
          font: headersFonts[header],
          key: header,
        });
    }
  }
  topHeaders.sort((a, b) => a.postition - b.postition);
  bottomHeaders.sort((a, b) => a.postition - b.postition);

  /*
   * ***************
   * **  Images ****
   * ***************
   */
  let leftSideImage = null;
  let centerImages = [];
  let rightSideImage = null;
  const logosPosition = Object.entries(bulletin.options.logosPosition);
  logosPosition.sort((a, b) => a[1][1] - b[1][1]);

  const imageScalers = {
    schoolLogo: bulletin?.options?.imageScalers?.schoolLogo || 1,
    govLogo: bulletin?.options?.imageScalers?.govLogo || 1,
    studentImage: bulletin?.options?.imageScalers?.studentImage || 1,
  }
  const scaleBySide = {
    left: 1,
    right: 1,
    center: [],
  }
  for (let [imgKey, imgPos] of logosPosition) {
    if (bulletin.headers[imgKey]) {
      switch (imgPos[0]) {
        case "l":
          leftSideImage = bulletin.headers[imgKey];
          scaleBySide.left = imageScalers[imgKey] || 1;
          break;
        case "r":
          rightSideImage = bulletin.headers[imgKey];
          scaleBySide.right = imageScalers[imgKey] || 1;
          break;
        case "c":
          centerImages.push(bulletin.headers[imgKey]);
          scaleBySide.center.push(imageScalers[imgKey] || 1);
      }
    }
  }
  if (rightSideImage) {
    const logoAutoWidth = getLogoWidth(rightSideImage, 17);
    doc.addImage(rightSideImage, "JPEG", pdf_width - 40, 2, logoAutoWidth * scaleBySide.right, 17 * scaleBySide.right);
    headerRY = 8 + 17 * scaleBySide.right;// was 25 now it's scaled so 25-17 ( old fixed height) = 8 + 17 * scaleBySide.right
  }
  if (leftSideImage) {
    const logoAutoWidth = getLogoWidth(leftSideImage, 17);
    doc.addImage(leftSideImage, "JPEG", 25, 2, logoAutoWidth * scaleBySide.left, 17 * scaleBySide.left);
    headerLY = 8 + 17 * scaleBySide.left;// was 25 now it's scaled so 25-17 ( old fixed height) = 8 + 17 * scaleBySide.right
  }
  if (centerImages.length > 0) {
    let addedImagesHeight = 0;
    if (centerImages.length == 1) {
      const logoAutoWidth = getLogoWidth(centerImages[0], 18);
      doc.addImage(
        centerImages[0],
        "JPEG",
        pdf_width / 2 - (logoAutoWidth * scaleBySide.center[0]) / 2,
        2,
        logoAutoWidth * scaleBySide.center[0],
        18 * scaleBySide.center[0]
      );
      addedImagesHeight = 18 * scaleBySide.center[0];
    } else if (centerImages.length == 2) {
      const logoAutoWidth1 = getLogoWidth(centerImages[0], 14);
      const logoAutoWidth2 = getLogoWidth(centerImages[1], 14);
      doc.addImage(
        centerImages[0],
        "JPEG",
        pdf_width / 2 + 1,
        6,
        logoAutoWidth1 * scaleBySide.center[0],
        14 * scaleBySide.center[0]
      );
      doc.addImage(
        centerImages[1],
        "JPEG",
        pdf_width / 2 - logoAutoWidth2 * scaleBySide.center[1] - 1,
        6,
        logoAutoWidth2 * scaleBySide.center[1],
        14 * scaleBySide.center[1]
      );
      addedImagesHeight = 14 * Math.max(scaleBySide.center[0], scaleBySide.center[1]);
    }
    const baseHeaderCY = centerImages.length == 1 ? 8 : 12
    // was 26 now it's scaled so 26-18 ( old fixed height) = 8 + 18 * scaleBySide.center[0] if centerImages.length == 1
    // if centerImages.length == 2 then 26-14 ( old fixed height) = 12 + 14 * scaleBySide.center[1]
    headerCY = baseHeaderCY + addedImagesHeight;
  }

  /*
   * ***************************************
   * ** Headers above trimester divider ****
   * ***************************************
   */
  // if gov text is used
  if (bulletin.headers.govText) {
    let govY = headerRY;
    let width;
    const hasRighttHeader =
      Object.entries(bulletin.options.headersPositions).filter(
        ([key, postition]) => postition[0] == "r" && bulletin.headers[key]
      ).length > 0 ||
      Object.entries(bulletin.options.logosPosition).filter(
        ([key, postition]) => postition[0] == "r" && bulletin.headers[key]
      ).length > 0;
    doc.setFont("PT Bold Heading", "Bold");
    doc.setFontSize(hasRighttHeader ? 9.5 : 12);
    doc.setTextColor(0, 0, 0);
    const govHeader = [
      ["الجمهورية التونسية", 18],
      ["وزارة التربية", 26],
      ["المندوبيّة الجهويّة للتربيّة", 12],
    ];
    const delegeRegional = data.schoolDetails.delegeRegional
      ? "بـ" + data.schoolDetails.delegeRegional
      : ".....................................بـ";
    if (hasRighttHeader) govHeader[2] = [govHeader[2][0] + delegeRegional, 2];
    for (let [text, i] of govHeader) {
      width = doc.getTextWidth(text);
      doc.text(text, pdf_width - width - i + 16, govY, { align: "center" });
      govY += hasRighttHeader ? 4 : 5;
    }
    if (!hasRighttHeader) {
      doc.text(delegeRegional, pdf_width - width + 8, govY, {
        align: "center",
      });
      headerRY = govY + 4;
    } else headerRY = govY + 2;
  }
  topHeaders.forEach((headerItem) => {
    try {
      // header Title
      doc.setFont(headerItem.font.title.font, headerItem.font.title.style);
      doc.setFontSize(headerItem.font.title.size);
      doc.setTextColor(...headerItem.font.title.color);
      let headerTitle = "";
      if (headerItem.key == "schoolName")
        headerTitle = headerTrasnlator.schoolName(data.selectedLevel);
      else if (headerItem.key == "studentName")
        headerTitle = headerTrasnlator.studentName(data.gender);
      else headerTitle = headerTrasnlator[headerItem.key];
      const Titlewidth = doc.getTextWidth(headerTitle);
      let headerTitleX = 0;
      let headerY = 0;
      let headerValueX = 0;
      const headerValue = data[headerItem.key];
      const headerValueWidth = doc.getTextWidth(headerValue);
      const yStep =
        Math.max(
          ...[headerItem.font.title.size, headerItem.font.value.size, 14]
        ) > 12
          ? 6
          : 4;
      switch (headerItem.orientation) {
        case "r":
          headerTitleX = pdf_width - Titlewidth - 12;
          headerY = headerRY;
          headerRY += yStep;
          headerValueX = headerTitleX - headerValueWidth;
          break;
        case "c":
          headerValueX = pdf_width / 2 - (Titlewidth + headerValueWidth) / 2;
          headerTitleX = headerValueX + headerValueWidth;
          headerY = headerCY;
          headerCY += yStep;
          break;
        case "l":
          headerTitleX = 10 + headerValueWidth;
          headerY = headerLY;
          headerLY += yStep;
          headerValueX = 10;
          break;
        default:
          headerTitleX = pdf_width - Titlewidth - 12;
          headerY = headerRY;
          headerRY += yStep;
          headerValueX = headerTitleX - headerValueWidth;
          break;
      }
      if (headerItem.font.lineReturn) {
        headerTitleX -=
          headerItem.orientation == "l" ? headerValueWidth / 2 : 0;
        headerValueX = headerTitleX + Titlewidth / 2 - headerValueWidth / 2;
      }
      doc.text(
        headerItem.font.lineReturn ? headerTitle.slice(2) : headerTitle,
        headerTitleX,
        headerY
      );
      if (headerItem.font.lineReturn) {
        if (headerItem.orientation == "r") headerRY += yStep;
        else if (headerItem.orientation == "l") headerLY += yStep;
        else if (headerItem.orientation == "c") headerCY += yStep;
        headerY += yStep;
      }

      // header value
      doc.setFont(headerItem.font.value.font, headerItem.font.value.style);
      doc.setFontSize(headerItem.font.value.size);
      doc.setTextColor(...headerItem.font.value.color);

      doc.text(headerValue, headerValueX, headerY);
    } catch (error) {
      console.error("Failed to print header : ", headerItem);
      console.info(error);
    }
  });

  /*
   * ***************************************
   * **  Trimester divider ****
   * ***************************************
   */
  const color = bulletin.colorPalette.main;
  const baseY =
    Math.max(headerCY + 2, headerRY, headerLY) +
    (bulletin.spacing?.trimersterLineMargin
      ? bulletin.spacing?.trimersterLineMargin
      : 0);
  bulletin.Y = baseY + 10; //? margin bellow trimester divider; 6pnt is the round buttom end + 4pts as margin
  const lineY = baseY;
  const roundY = baseY - 6;
  const trimesterY = baseY + 2.5;

  let title = bulletin.headers.trimHeader || "الثلاثي";
  switch (String(data.selectedTrimester)) {
    case "1" || 1:
      title += " الأول";
      break;
    case "2" || 2:
      title += " الثاني";
      break;
    case "3" || 3:
      title += " الثالث";
      break;
  }
  doc.setFontSize(16);
  doc.setFont("Noto Sans Arabic", "Bold");
  const mindTitleWidth = doc.getTextWidth("الثلاثي الأول");
  const width = doc.getTextWidth(title);
  const roundRectWidth = Math.max(mindTitleWidth, width);
  doc.setDrawColor(...color);
  doc.setLineWidth(0.8);
  doc.line(0, lineY, pdf_width, lineY);
  // trimester rounded rect title
  doc.setLineWidth(0.4);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(pdf_width / 2 - roundRectWidth / 2 - 10, roundY, roundRectWidth + 20, 12, 4, 4, "FD");
  // trimester text
  doc.setTextColor(...color);
  doc.text(title, pdf_width / 2 - roundRectWidth / 2, trimesterY);

  /*
   * ***************************************
   * ** Headers below trimester divider ****
   * ***************************************
   */
  let xStep = 4;
  bulletin.X = 10;
  if (bottomHeaders.length > 0)
    bulletin.Y +=
      Math.max(
        ...[
          bottomHeaders[0].font.title.size,
          bottomHeaders[0].font.value.size,
          14,
        ]
      ) > 12
        ? 2
        : 4;
  bottomHeaders.forEach((headerItem) => {
    try {
      let headerTitle = "";
      if (headerItem.key == "schoolName")
        headerTitle = headerTrasnlator.schoolName(data.selectedLevel);
      else if (headerItem.key == "studentName")
        headerTitle = headerTrasnlator.studentName(data.gender);
      else headerTitle = headerTrasnlator[headerItem.key];
      const Titlewidth = doc.getTextWidth(headerTitle);
      const headerValue = data[headerItem.key] || "";
      const headerValueWidth = doc.getTextWidth(headerValue);
      if ([3, 2].includes(bottomHeaders.length)) {
        if (headerItem.postition == 0) bulletin.X = 10;
        else if (headerItem.postition == 1 && bottomHeaders.length == 3)
          bulletin.X = pdf_width / 2 - (Titlewidth + headerValueWidth) / 2;
        else if (
          headerItem.postition == 2 ||
          (headerItem.postition == 1 && bottomHeaders.length == 2)
        )
          bulletin.X = pdf_width - Titlewidth - headerValueWidth - 12;
      }

      let headerValueX = bulletin.X;
      let headerTitleX = headerValueX + headerValueWidth + 2;
      //bulletin.X =
      //  bulletin.X == 10 ? Titlewidth : headerTitleX + Titlewidth + xStep;
      bulletin.X = headerTitleX + Titlewidth + xStep;
      if (bulletin.X > pdf_width - 1) {
        // breaking line and reseting X to first value
        bulletin.Y +=
          Math.max(
            ...[headerItem.font.title.size, headerItem.font.value.size, 14]
          ) > 12
            ? 6
            : 4;
        bulletin.X = 10;
        headerValueX = bulletin.X;
        headerTitleX = headerValueX + headerValueWidth + 1;
      }
      // printing header title
      doc.setFont(headerItem.font.title.font, headerItem.font.title.style);
      doc.setFontSize(headerItem.font.title.size);
      doc.setTextColor(...headerItem.font.title.color);

      doc.text(headerTitle, headerTitleX, bulletin.Y);
      // printing header value
      doc.setFont(headerItem.font.value.font, headerItem.font.value.style);
      doc.setFontSize(headerItem.font.value.size);
      doc.setTextColor(...headerItem.font.value.color);

      doc.text(headerValue, headerValueX, bulletin.Y);
    } catch (error) {
      console.error("Failed to print header : ", headerItem);
      console.info(error);
    }
  });
  if (bottomHeaders.length > 0) bulletin.Y += 8;
  bulletin.doc = doc;

  return bulletin;
}
/**
 * draw side function, used to draw the side part of bulletin
 * @param {Bulletin} bulletin - Bulletin object
 * @param {
 * normal:{
 * moy:String
 * text:String
 * rank:String
 * min:String
 * max:String
 * }
 * peda: {
 * moy:String
 * text:String
 * rank:String
 * min:String
 * max:String
 * }
 * personalized:{
 * moy:String
 * text:String
 * rank:String
 * min:String
 * max:String
 * }
 * appreciation:{
 * title:String
 * value:String
 * }
 * }data
 */
async function drawSide(bulletin, data, details) {
  let text, width;
  const doc = bulletin.doc;
  const ctx = bulletin.ctx;
  const bulletinColors = bulletin.colorPalette;
  let yPosition = bulletin.Y;
  const value = 5;
  function drawMoy(
    data = {
      on: "normal",
      moy: "",
      text: "",
      rank: null,
      min: null,
      max: null,
      cornor: {},
    }
  ) {
    const { cornor } = data;
    const nbDisabled = [data.rank, data.min, data.max].filter(
      (value) => value === null
    ).length;
    const elemts = [];
    const values = [];
    const valuesColors = [];
    if (data.min) {
      elemts.push("أدنى\nمعدل بالقسم");
      values.push(data.min);
      valuesColors.push(bulletinColors[data.on].min);
    }
    if (data.max) {
      elemts.push("أعلى\nمعدل بالقسم");
      values.push(data.max);
      valuesColors.push(bulletinColors[data.on].max);
    }
    if (data.rank) {
      elemts.push("الرتبة");
      values.push(data.rank);
      valuesColors.push(bulletinColors[data.on].rank);
    }
    // when only moy if shown
    if (nbDisabled == 3) {
      doc.setFillColor(...bulletinColors.main);
      doc.setDrawColor(...bulletinColors.secondary);
      roundRect(
        bulletinColors.main,
        ctx,
        6,
        yPosition,
        57.5,
        8.2,
        cornor,
        true,
        false
      );
      doc.setFillColor(...bulletinColors.secondary);
      doc.setDrawColor(...bulletinColors.secondaryBorder);
      doc.setLineWidth(0.05);
      doc.rect(6, yPosition + 9, 57.5, 8.2, "FD");
      doc.setFontSize(10);
      text = data.text;
      width = doc.getTextWidth(text);
      doc.setTextColor(...bulletinColors.mainBorder);
      doc.text(text, 45 - width / 2 - 10, yPosition + 5.3);

      doc.setFontSize(11);
      width = doc.getTextWidth(data.moy || "--");
      doc.setTextColor(...bulletinColors[data.on].moy);
      doc.text(data.moy, 45 - width / 2 - 10, yPosition + 14.5);

      yPosition += 20;
    }
    // if two fields are shown
    if (nbDisabled == 1) {
      doc.setFillColor(...bulletinColors.main);
      doc.setDrawColor(...bulletinColors.mainBorder);
      roundRect(
        bulletinColors.secondary,
        ctx,
        45 - value * 2,
        yPosition,
        33.5 - value,
        8.2,
        cornor,
        true,
        false
      );
      doc.setFont("Amiri", "Bold");
      doc.setFontSize(11);
      doc.setTextColor(...bulletinColors.mainBorder);
      text = data.text;
      width = doc.getTextWidth(text);
      doc.text(text, 65.5 - value * 3 - width / 2, yPosition + 5.3);
      doc.setLineWidth(0.2);
      doc.setFillColor(...bulletinColors.secondary);
      doc.setDrawColor(...bulletinColors.secondaryBorder);

      roundRect(
        bulletinColors.secondaryBorder,
        ctx,
        6,
        yPosition,
        38 - value * 2,
        7.8,
        cornor,
        true,
        true
      );
      roundRect(
        bulletinColors.secondaryBorder,
        ctx,
        6,
        yPosition,
        19 - value,
        7.8,
        cornor,
        true,
        true
      );

      doc.setFont("Amiri", "Bold");
      doc.setFontSize(7);
      doc.setTextColor(...bulletinColors.secondaryBorder);
      doc.text(elemts[0], 35 - (value + value / 2), yPosition + 3.3, {
        align: "center",
      });
      doc.text(elemts[1], 16.2 - value / 2, yPosition + 3.3, {
        align: "center",
      });

      // moyenne rect
      doc.setFillColor(...bulletinColors.secondary);
      doc.setDrawColor(...bulletinColors.secondaryBorder);
      doc.setLineWidth(0.15);
      doc.rect(45 - value * 2, yPosition + 9.3, 33.5 - value, 16.4, "FD");
      doc.setFillColor(...bulletinColors.tertiary);
      doc.setDrawColor(...bulletinColors.tertiaryBorder);
      doc.rect(6, yPosition + 9.3, 19 - value, 16.4, "FD");
      doc.rect(25 - value, yPosition + 9.3, 19 - value, 16.4, "FD");

      // moyenne text
      doc.setTextColor(...bulletinColors[data.on].moy);
      doc.setFontSize(16);
      text = String(data.moy);
      width = doc.getTextWidth(text);
      doc.text(
        text,
        63 - width / 2 - (value * 2 + value / 2),
        yPosition + 18.3
      );

      doc.setFontSize(13);
      doc.setTextColor(...valuesColors[0]);
      text = String(values[0]);
      width = doc.getTextWidth(text);
      doc.text(text, 34.75 - width / 2 - (value + value / 2), yPosition + 18.3);

      text = String(values[1]);
      doc.setTextColor(...valuesColors[1]);
      width = doc.getTextWidth(text);
      doc.text(text, 15.25 - width / 2 - value / 2, yPosition + 18.3);
      yPosition += 27; //99.2
    }
    // if one field are shown
    if (nbDisabled == 2) {
      doc.setFillColor(...bulletinColors.main);
      doc.setDrawColor(...bulletinColors.mainBorder);
      roundRect(
        bulletinColors,
        ctx,
        31 - value * 2,
        yPosition,
        47.5 - value,
        8.2,
        cornor,
        true,
        false
      );
      doc.setFont("Amiri", "Bold");
      doc.setFontSize(9);
      doc.setTextColor(...bulletinColors.mainBorder);
      text = data.text;
      width = doc.getTextWidth(text);
      doc.text(text, 58 - value * 3 - width / 2, yPosition + 5.3);
      doc.setLineWidth(0.2);
      doc.setFillColor(...bulletinColors.secondary);
      doc.setDrawColor(...bulletinColors.secondaryBorder);

      roundRect(
        bulletinColors.secondaryBorder,
        ctx,
        6,
        yPosition,
        19 - value,
        7.8,
        cornor,
        true,
        true
      );

      doc.setFont("Amiri", "Bold");
      doc.setFontSize(7);
      doc.setTextColor(...bulletinColors.secondaryBorder);
      doc.text(elemts[0], 16.2 - value / 2, yPosition + 3.3, {
        align: "center",
      });

      // moyenne rect
      doc.setFillColor(...bulletinColors.secondary);
      doc.setDrawColor(...bulletinColors.secondaryBorder);
      doc.setLineWidth(0.15);
      doc.rect(31 - value * 2, yPosition + 9.3, 47.5 - value, 16.4, "FD");
      doc.setDrawColor(...bulletinColors.tertiaryBorder);
      doc.setFillColor(...bulletinColors.tertiary);
      doc.rect(6, yPosition + 9.3, 19 - value, 16.4, "FD");
      //doc.rect(25 - value, yPosition + 9.3, 19 - value, 16.4, "FD");

      // moyenne text
      doc.setTextColor(...bulletinColors[data.on].moy);
      doc.setFontSize(16);
      text = String(data.moy);
      width = doc.getTextWidth(text);
      doc.text(
        text,
        55 - width / 2 - (value * 2 + value / 2),
        yPosition + 18.3
      );
      doc.setTextColor(...valuesColors[0]);
      doc.setFontSize(13);
      text = String(values[0]);
      width = doc.getTextWidth(text);
      doc.text(text, 15.25 - width / 2 - value / 2, yPosition + 18.3);
      yPosition += 27; //99.2
    }
    // if all field are shown
    if (nbDisabled == 0) {
      doc.setFillColor(...bulletinColors.main);
      doc.setDrawColor(...bulletinColors.mainBorder);
      roundRect(
        bulletinColors.secondary,
        ctx,
        45 - value * 2,
        yPosition,
        33.5 - value,
        8.2,
        cornor,
        true,
        false
      );
      doc.setFont("Amiri", "Bold");
      doc.setFontSize(11);
      doc.setTextColor(...bulletinColors.mainBorder);
      text = data.text;
      width = doc.getTextWidth(text);
      doc.text(text, 65.5 - value * 3 - width / 2, yPosition + 5.3);
      doc.setLineWidth(0.2);
      doc.setFillColor(...bulletinColors.secondary);
      doc.setDrawColor(...bulletinColors.secondaryBorder);

      roundRect(
        bulletinColors.secondaryBorder,
        ctx,
        6,
        yPosition,
        38 - value * 2,
        7.8,
        cornor,
        true,
        true
      );
      roundRect(
        bulletinColors.secondaryBorder,
        ctx,
        6,
        yPosition,
        19 - value,
        7.8,
        {},
        cornor,
        true
      );

      doc.setFont("Amiri", "Bold");
      doc.setFontSize(7);
      doc.setTextColor(...bulletinColors.secondaryBorder);
      doc.text(elemts[0], 35 - (value + value / 2), yPosition + 3.3, {
        align: "center",
      });
      doc.text(elemts[1], 16.2 - value / 2, yPosition + 3.3, {
        align: "center",
      });

      // moyenne rect
      doc.setFillColor(...bulletinColors.secondary);
      doc.setDrawColor(...bulletinColors.secondaryBorder);
      doc.setLineWidth(0.15);
      doc.rect(45 - value * 2, yPosition + 9.3, 33.5 - value, 16.4, "FD");
      doc.setFillColor(...bulletinColors.tertiary);
      doc.setDrawColor(...bulletinColors.tertiaryBorder);
      doc.rect(6, yPosition + 9.3, 19 - value, 16.4, "FD");
      doc.rect(25 - value, yPosition + 9.3, 19 - value, 16.4, "FD");

      // moyenne text
      doc.setTextColor(...bulletinColors[data.on].moy);
      doc.setFontSize(16);
      text = String(data.moy);
      width = doc.getTextWidth(text);
      doc.text(
        text,
        63 - width / 2 - (value * 2 + value / 2),
        yPosition + 18.3
      );
      doc.setFontSize(13);
      doc.setTextColor(...valuesColors[0]);
      text = String(values[0]);
      width = doc.getTextWidth(text);
      doc.text(text, 34.75 - width / 2 - (value + value / 2), yPosition + 18.3);

      text = String(values[1]);
      doc.setTextColor(...valuesColors[1]);
      width = doc.getTextWidth(text);
      doc.text(text, 15.25 - width / 2 - value / 2, yPosition + 18.3);
      yPosition += 27; //99.2
    }
  }
  /*
   * *******************
   * ** Moy ************
   * *******************
   */
  for (let moyType of ["normal", "peda", "personalized"]) {
    if (bulletin.side[moyType].allow) {
      const moy = data[moyType].moy;
      drawMoy({
        on: moyType,
        moy: moy ? (moy == "" ? "--" : moy) : "--",
        text: data[moyType].text,
        rank: bulletin.side[moyType].rank ? data[moyType].rank : null,
        min: bulletin.side[moyType].min ? data[moyType].min : null,
        max: bulletin.side[moyType].max ? data[moyType].max : null,
        cornor: bulletin.pedaLike ? { upperRight: 7.8 } : {},
      });
    }
  }
  /*
   * ****************************
   * ** Admin Appreciation ******
   * ****************************
   */
  if (bulletin.side.adminAppreciation) {
    const customAdminAppreciationFonts = bulletin.side.adminAppreciationFont;
    let startY = yPosition + 2;
    doc.setFillColor(...bulletinColors.secondary);
    doc.setDrawColor(...bulletinColors.secondaryBorder);
    doc.setTextColor(...bulletinColors.secondaryBorder);

    doc.rect(6, 4 + startY, 72.5 - value * 3, 30); // 115 + y
    doc.rect(8, startY, 67.5 - value * 3, 8, "FD");
    doc.setFont(
      customAdminAppreciationFonts?.font || "Amiri",
      customAdminAppreciationFonts?.style || "Bold"
    );
    doc.setFontSize(11 - value / 2);
    doc.setTextColor(...bulletinColors.secondaryBorder);
    text = "ملاحظات المدير)ة( حول السّلوك و المواظبة";
    doc.text(text, 12, 3 + startY);

    doc.setTextColor(67, 62, 63);
    doc.setFontSize(10);
    if (customAdminAppreciationFonts) {
      // set custom fonts
      doc.setFontSize(customAdminAppreciationFonts?.size || 10);
      doc.setTextColor(
        ...(typeof customAdminAppreciationFonts?.color == "string"
          ? hexToRgb(customAdminAppreciationFonts?.color)
          : [67, 62, 63])
      );
      doc.setFont(
        customAdminAppreciationFonts?.font || "Amiri",
        customAdminAppreciationFonts?.style || "normal"
      );
    }
    const tt = formatArabicText(data.adminAppreciation);
    doc.text(String(tt), 35, 14 + startY, {
      maxWidth: /[a-zA-Z]/.test(tt) ? 50 : 60,
      align: "center",
    }); //reset after use custom fonts
    if (customAdminAppreciationFonts) {
      doc.setFontSize(10);
      doc.setFont("Amiri", "normal");
      doc.setTextColor(67, 62, 63);
    }
    yPosition += 36;
  }
  /*
   * *******************
   * ** Certif *********
   * *******************
   */
  doc.setFontSize(13);
  doc.setFont("Amiri", "normal");
  if (bulletin.side.certif) {
    const customCertifFonts = bulletin.side.certifFont;
    doc.setFillColor(...bulletinColors.secondary);
    doc.setDrawColor(...bulletinColors.secondaryBorder);

    yPosition -= 36;

    doc.rect(6, yPosition + 42, 72.5 - value * 3, 20);
    doc.rect(40.75 - value * 3, yPosition + 38, 36.75, 8, "FD");

    doc.setFontSize(13);

    doc.setTextColor(...bulletinColors.secondaryBorder);

    text = "الشّهادة";
    doc.text(text, 55 - value * 3, yPosition + 43);

    doc.setTextColor(67, 62, 63);
    if (customCertifFonts) {
      // set custom fonts
      doc.setFontSize(customCertifFonts?.size || 13);
      doc.setTextColor(
        ...(typeof customCertifFonts?.color == "string"
          ? hexToRgb(customCertifFonts?.color)
          : [67, 62, 63])
      );
      doc.setFont(
        customCertifFonts?.font || "Amiri",
        customCertifFonts?.style || "normal"
      );
    }
    doc.text(
      getCertificationNameByMoyenne(bulletin.side?.certifUsePersonalizedMoy === true ? data.personalized?.moy : data.normal?.moy, details.certifs),
      40 - value * 3,
      yPosition + 55
    ); //reset after use custom fonts
    if (customCertifFonts) {
      doc.setFontSize(13);
      doc.setFont("Amiri", "normal");
      doc.setTextColor(67, 62, 63);
    }
    yPosition += 65;
  }
  //مدير)ة( المدرسة"
  doc.setLineWidth(0.15);

  doc.setFillColor(...bulletinColors.secondary);
  doc.setDrawColor(...bulletinColors.secondaryBorder);

  yPosition -= 36;

  doc.rect(6, yPosition + 42, 72.5 - value * 3, 55);
  doc.rect(40.75 - value * 3, yPosition + 38, 36.75, 8, "FD");

  doc.rect(6, yPosition + 106, 72.5 - value * 3, 30);
  doc.rect(40.75 - value * 3, yPosition + 102, 36.75, 8, "FD");

  doc.setFontSize(13);

  doc.setTextColor(...bulletinColors.secondaryBorder);

  text = details.selectedLevel > 9 ? "مدير)ة( المعهد" : "مدير)ة( المدرسة";
  doc.text(text, 45 - value * 3, yPosition + 43);

  doc.setTextColor(67, 62, 63);
  text = details.headMaster;
  if (
    details.showDate !== undefined && details.showDate !== null
      ? details.showDate
      : bulletin.side.printDate
  )
    text += "\n" + moment(details.date).format("YYYY/MM/DD");
  doc.text(text, 30 - value * 3, yPosition + 51);

  doc.setTextColor(...bulletinColors.secondaryBorder);
  doc.setFontSize(16);

  text = "إمضاء الولي";
  doc.text(text, 48 - value * 3, yPosition + 107);

  /*
   * *******************
   * ** stamp *********
   * *******************
   */
  if (bulletin.side.stamp) {
    try {
      const scale = bulletin.options?.imageScalers?.stamp || 1;
      doc.addImage(bulletin.side.stamp, "PNG", 10, yPosition + 61, 30 * scale, 30 * scale);
    } catch (e) { }
  }

  /*
   * *******************
   * ** QR code ********
   * *******************
   */
  if (bulletin.qrCode) {
    try {
      const lastYPosition = yPosition + 135;
      const avaibleSpace = bulletin.pdf_height - lastYPosition - 15;
      const QR_Size = avaibleSpace > 50 * (bulletin.options?.imageScalers?.qrCode || 1) ? 45 * (bulletin.options?.imageScalers?.qrCode || 1) : avaibleSpace;
      const QR_Y = lastYPosition + 10;
      doc.addImage(bulletin.qrCode, "PNG", 10, QR_Y, QR_Size, QR_Size);
    } catch (e) {
      console.info(e);
    }
  }

  bulletin.doc = doc;
  bulletin.ctx = ctx;
  return bulletin;
}
// Helper function to format Arabic text with proper parentheses
const formatArabicText = (text, maxMark = null) => {
  if (!text) return text;

  // Normalize the text to ensure proper handling of diacritical marks
  const normalizedText = text.normalize("NFC");

  // Check if text contains Arabic characters
  const arabicLetters = /[\u0600-\u06FF]/;
  const isArabic = arabicLetters.test(normalizedText);

  if (isArabic) {
    if (maxMark) {
      // For Arabic text with max mark, put parentheses at the start
      return `(${maxMark}/) ${normalizedText}`;
    }
    return normalizedText;
  } else {
    // For non-Arabic text, keep parentheses at the end
    return maxMark ? `${normalizedText} (/${maxMark})` : normalizedText;
  }
};

const speceficStyle = (field) => {
  let size = false;
  let color = false;
  if (field && field.auto === false) {
    size = field.font || false;
    color = field.color || false;
  }
  return [size, color];
};
/**
 * draw side function, used to draw the side part of bulletin
 * @param {Bulletin} bulletin - Bulletin object
 * @param {Object} studentData
 * @param {Number} studentCount
 * @param {
 * selectedLevel:Number
 * } details
 */
async function drawBody(bulletin, studentData) {
  const doc = bulletin.doc;
  const ctx = bulletin.ctx;
  if (bulletin.body.fuseSubjects) studentData = fuseSubSubjects(studentData);
  const spacingBetweenModules = decideSpacing(
    bulletin.Y,
    bulletin.pdf_height - bulletin.spacing.bottomLimit,
    studentData.modules
  ).spacingBetweenModules;
  let currentY = bulletin.Y;

  let factorAddition = bulletin.factorAddition;
  const { forceOnePage, stretchFullPage } = bulletin.spacing;
  const augmentationFactor = forceOnePage ? -1 : stretchFullPage ? 1 : 0; // -1 reducing size, 1 augmenting size, 0 none
  if (!bulletin.factorAdditionMutated && (forceOnePage || stretchFullPage)) {
    let continueLoop = true;
    let firstEntry = true;
    const factorLimit = forceOnePage ? 0.5 : 0.15;
    while (factorAddition <= factorLimit && (firstEntry || continueLoop)) {
      let tempPDF = new JsPDF();
      tempPDF.addPage();
      let tempCtx = tempPDF.context2d;
      const autoTableResutt = getModuleBodyAutoTable(
        bulletin,
        tempPDF,
        tempCtx,
        studentData.modules,
        spacingBetweenModules,
        augmentationFactor,
        factorAddition,
        currentY,
        continueLoop,
        firstEntry
      );
      tempPDF = autoTableResutt.tempPDF;
      tempCtx = autoTableResutt.tempCtx;
      factorAddition = autoTableResutt.factorAddition;
      continueLoop = autoTableResutt.continueLoop;
      firstEntry = false;
    }
    bulletin.factorAdditionMutated = true;
  }
  const autoTableResutt = getModuleBodyAutoTable(
    bulletin,
    doc,
    ctx,
    studentData.modules,
    spacingBetweenModules,
    augmentationFactor,
    factorAddition,
    currentY,
    false,
    false
  );
  bulletin.factorAddition = factorAddition;
  bulletin.doc = autoTableResutt.tempPDF;
  bulletin.ctx = autoTableResutt.tempCtx;
  return bulletin;
}
async function printBulletin(
  affichageData,
  studentData,
  bulletinRawData,
  schoolData,
  preview = false
) {
  let bulletinData = cloneDeep(bulletinRawData);
  if (!affichageData || Object.keys(affichageData).length === 0) {
    affichageData = defaultTemplate;
  }

  if (affichageData.side.stamp)
    affichageData.side.stamp = schoolData.resourcesUrl.replace(
      "FILE_PATH",
      affichageData.side.stamp
    );
  if (affichageData.headers.schoolLogo) {
    try {
      const arrayOfImages = schoolData.image;
      for (var i = 0; i < arrayOfImages.length; i++) {
        if (schoolData.activeBuilding === arrayOfImages[i].db) {
          affichageData.headers.schoolLogo = arrayOfImages[i].logo;
        }
      }
    } catch (error) {
      console.error("Error loading school logo:", error);
    }
  }
  if (affichageData.headers.govLogo) {
    const customGovLogoURL = affichageData.headers.customGovLogo
    if (customGovLogoURL) {
      const customGovLogoValue = affichageData.headers.customGovLogo;
      if (typeof customGovLogoValue.name == "string") {
        try {
          affichageData.headers.govLogo = await getImageBase64(customGovLogoValue);
        } catch {
          affichageData.headers.govLogo = null;
        }
      } else if (customGovLogoValue && !customGovLogoValue.includes("http")) {
        affichageData.headers.govLogo = schoolData.resourcesUrl.replace(
          "FILE_PATH",
          customGovLogoValue
        );
      }
    }
    else affichageData.headers.govLogo = "/resources/images/govImg.png";
  }
  if (affichageData.headers.studentImage && studentData.photo) {
    affichageData.headers.studentImage = schoolData.resourcesUrl.replace(
      "FILE_PATH",
      studentData.photo
    );
  }
  if (affichageData.bg_image) {
    if (typeof affichageData.bg_image.name == "string") {
      try {
        affichageData.bg_image = await getImageBase64(affichageData.bg_image);
      } catch {
        affichageData.bg_image = null;
      }
    } else if (
      affichageData.bg_image &&
      !affichageData.bg_image.includes("http")
    ) {
      affichageData.bg_image = schoolData.resourcesUrl.replace(
        "FILE_PATH",
        affichageData.bg_image
      );
    }
  }
  if (affichageData.qrCode) {
    if (typeof affichageData.qrCode.name == "string") {
      try {
        affichageData.qrCode = await getImageBase64(affichageData.qrCode);
      } catch {
        affichageData.qrCode = null;
      }
    } else if (affichageData.qrCode && !affichageData.qrCode.includes("http")) {
      affichageData.qrCode = schoolData.resourcesUrl.replace(
        "FILE_PATH",
        affichageData.qrCode
      );
    }
  }
  const schoolDetails = schoolData.building.filter(
    (el) => el.dbName == schoolData.activeBuilding
  )[0];
  let { start, end } = schoolData.schoolarYears.find(
    (el) => el._id == schoolData.activeSchoolarYear
  );

  start = new Date(start).getFullYear();
  end = new Date(end).getFullYear();
  let bulletin = new Bulletin(
    affichageData.type,
    affichageData.colorPalette,
    affichageData.bg_image,
    affichageData.qrCode,
    affichageData.pedaLike,
    affichageData.headers,
    affichageData.body,
    affichageData.side,
    affichageData.spacing,
    affichageData.options,
    true
  );
  await bulletin.loadImages();
  bulletin = await drawHeader(bulletin, {
    schoolName: schoolDetails.ArabicName
      ? schoolDetails.ArabicName
      : "..............................",
    studentName: bulletinData.fullName,
    birthDate: moment(bulletinData.BirthDate).format("DD/MM/yyyy") || " ",
    uniqueIden: bulletinData.uniqueId || "",
    schoolarYear: end + " / " + start,
    className: bulletinData.classroomName,
    gender: bulletinData.gender,
    nbInscription: String(bulletinData.students) || "",
    selectedTrimester: affichageData.trimester,
    selectedLevel: affichageData.level,
    schoolDetails: {
      delegeRegional: schoolDetails.delegeRegional,
    },
  });
  bulletin = await drawSide(
    bulletin,
    {
      normal: {
        moy: bulletinData.moyenne,
        text: affichageData.side?.normal?.name || "--",
        min: bulletinData.minMoyenne || "--",
        max: bulletinData.maxMoyenne || "--",
        rank: bulletinData.rank || "--",
      },
      peda: {
        moy: bulletinData.pedaMoyenne,
        text: affichageData.side?.peda?.name || "--",
        min: bulletinData.pedaMinMoyenne || "--",
        max: bulletinData.pedaMaxMoyenne || "--",
        rank: bulletinData.pedagogicMoyenneRank || "--",
      },
      personalized: {
        moy: bulletinData.personalizedMoyenne,
        text: affichageData.side?.personalized?.name || "--",
        min: bulletinData.personalizedMoyenneMin || "--",
        max: bulletinData.personalizedMoyenneMax || "--",
        rank: bulletinData.personalizedRank || "--",
      },
      appreciation: {
        title: "",
        value: "",
      },
      adminAppreciation: bulletinData.adminAppreciation,
    },
    {
      headMaster: schoolDetails.headMaster,
      selectedLevel: affichageData.level,
      date: schoolData.date || new Date(),
      certifs: schoolData.certifs || [],
      showDate: schoolData.showDate,
    }
  );
  bulletin = await drawBody(bulletin, bulletinData);
  if (preview) {
    const bloburl = bulletin.doc.output("bloburl");
    bulletin = null; // releasing the ref on bulletin object to be freed from memory
    return bloburl;
  } else {
    let typeLabel = "";
    if (affichageData.type === "MT") typeLabel = "Pilote";
    if (affichageData.type === "MS") typeLabel = "Spécifique";
    bulletin.doc.save(`Bulletin ${typeLabel} ${bulletinData.fullName}.pdf`);
  }
  bulletin = null;
}
async function printAllBulletin(
  affichageData,
  studentsData,
  bulletinsData,
  schoolData,
  preview = false
) {
  const allowStudentImage = affichageData.headers.studentImage;

  // Prepare common data
  if (affichageData.side.stamp) {
    affichageData.side.stamp = schoolData.resourcesUrl.replace(
      "FILE_PATH",
      affichageData.side.stamp
    );
  }

  if (affichageData.headers.schoolLogo) {
    const arrayOfImages = schoolData.image;
    for (var i = 0; i < arrayOfImages.length; i++) {
      if (schoolData.activeBuilding === arrayOfImages[i].db) {
        affichageData.headers.schoolLogo = arrayOfImages[i].logo;
      }
    }
  }

  if (affichageData.headers.govLogo) {
    affichageData.headers.govLogo = "/resources/images/govImg.png";
  }

  if (affichageData.bg_image) {
    if (typeof affichageData.bg_image.name == "string") {
      try {
        affichageData.bg_image = await getImageBase64(affichageData.bg_image);
      } catch {
        affichageData.bg_image = null;
      }
    } else if (
      affichageData.bg_image &&
      !affichageData.bg_image.includes("http")
    ) {
      affichageData.bg_image = schoolData.resourcesUrl.replace(
        "FILE_PATH",
        affichageData.bg_image
      );
    }
  }

  if (affichageData.qrCode) {
    if (typeof affichageData.qrCode.name == "string") {
      try {
        affichageData.qrCode = await getImageBase64(affichageData.qrCode);
      } catch {
        affichageData.qrCode = null;
      }
    } else if (affichageData.qrCode && !affichageData.qrCode.includes("http")) {
      affichageData.qrCode = schoolData.resourcesUrl.replace(
        "FILE_PATH",
        affichageData.qrCode
      );
    }
  }
  if (affichageData.headers.govLogo) {
    const customGovLogoURL = affichageData.headers.customGovLogo
    if (customGovLogoURL) {
      const customGovLogoValue = affichageData.headers.customGovLogo;
      if (typeof customGovLogoValue.name == "string") {
        try {
          affichageData.headers.govLogo = await getImageBase64(customGovLogoValue);
        } catch {
          affichageData.headers.govLogo = null;
        }
      } else if (customGovLogoValue && !customGovLogoValue.includes("http")) {
        affichageData.headers.govLogo = schoolData.resourcesUrl.replace(
          "FILE_PATH",
          customGovLogoValue
        );
      }
    }
    else affichageData.headers.govLogo = "/resources/images/govImg.png";
  }

  const schoolDetails = schoolData.building.filter(
    (el) => el.dbName == schoolData.activeBuilding
  )[0];

  let { start, end } = schoolData.schoolarYears.find(
    (el) => el._id == schoolData.activeSchoolarYear
  );

  start = new Date(start).getFullYear();
  end = new Date(end).getFullYear();

  // Create a single bulletin instance for all students
  let bulletin = new Bulletin(
    affichageData.type,
    affichageData.colorPalette,
    affichageData.bg_image,
    affichageData.qrCode,
    affichageData.pedaLike,
    affichageData.headers,
    affichageData.body,
    affichageData.side,
    affichageData.spacing,
    affichageData.options,
    true
  );
  await bulletin.loadImages();

  // Process each student
  let studentsImage = [];
  if (allowStudentImage)
    studentsImage = await bulletin.reloadStudentsImage(
      schoolData,
      studentsData
    );
  else bulletin.headers.studentImage = false;
  for (let i = 0; i < bulletinsData.length; i++) {
    bulletin.setBG();
    const studentData = studentsData[i];
    const bulletinData = bulletinsData[i];
    if (allowStudentImage) {
      const studentImage = studentsImage.find(
        (imageInfo) => imageInfo.id == bulletinData.id
      );
      // Add student photo if available
      bulletin.headers.studentImage = studentImage?.photo || false;
    }

    // Draw header for current student
    bulletin = await drawHeader(bulletin, {
      schoolName: schoolDetails.ArabicName
        ? schoolDetails.ArabicName
        : "..............................",
      studentName: bulletinData.fullName,
      birthDate: moment(bulletinData.BirthDate).format("DD/MM/yyyy") || " ",
      uniqueIden: bulletinData.uniqueId || "",
      schoolarYear: end + " / " + start,
      className: bulletinData.classroomName,
      gender: bulletinData.gender,
      nbInscription: String(bulletinData.students) || "",
      selectedTrimester: affichageData.trimester,
      selectedLevel: affichageData.level,
      schoolDetails: {
        delegeRegional: schoolDetails.delegeRegional,
      },
    });

    // Draw side information
    bulletin = await drawSide(
      bulletin,
      {
        normal: {
          moy: bulletinData.moyenne,
          text: affichageData.side?.normal?.name || "--",
          min: bulletinData.minMoyenne || "--",
          max: bulletinData.maxMoyenne || "--",
          rank: bulletinData.rank || "--",
        },
        peda: {
          moy: bulletinData.pedaMoyenne,
          text: affichageData.side?.peda?.name || "--",
          min: bulletinData.pedaMinMoyenne || "--",
          max: bulletinData.pedaMaxMoyenne || "--",
          rank: bulletinData.pedagogicMoyenneRank || "--",
        },
        personalized: {
          moy: bulletinData.personalizedMoyenne,
          text: affichageData.side?.personalized?.name || "--",
          min: bulletinData.personalizedMoyenneMin || "--",
          max: bulletinData.personalizedMoyenneMax || "--",
          rank: bulletinData.personalizedRank || "--",
        },
        appreciation: {
          title: "",
          value: "",
        },
        adminAppreciation: bulletinData.adminAppreciation,
      },
      {
        headMaster: schoolDetails.headMaster,
        selectedLevel: affichageData.level,
        date: schoolData.date || new Date(),
        certifs: schoolData.certifs || [],
        showDate: schoolData.showDate,
      }
    );

    // Draw body content
    bulletin = await drawBody(bulletin, bulletinData);

    // Add new page for next student if not the last one
    if (i < bulletinsData.length - 1) {
      bulletin.doc.addPage();
    }
  }
  // After usage: clearing image array for memory overhead protection and optimization perpuse
  studentsImage.forEach((obj) => {
    obj.photo = null; // Clear base64 string
  });
  studentsImage = null; // Clear images array reference

  if (preview) {
    const bloburl = bulletin.doc.output("bloburl");
    bulletin = null; // releasing the ref on bulletin object to be freed from memory
    return bloburl;
  } else {
    let typeLabel = "";
    if (affichageData.type === "MT") typeLabel = "Pilote";
    if (affichageData.type === "MS") typeLabel = "Spécifique";
    bulletin.doc.save(
      `Bulletins ${typeLabel} ${bulletinsData[0]?.classroomName || ""}.pdf`
    );
    bulletin = null; // clear bulletin object
  }
}

export default {
  printBulletin,
  printAllBulletin,
  Bulletin,
  drawHeader,
  drawSide,
  drawBody,
  rgbToHex,
  hexToRgb,
}
